/**
 * PaymentsPage: Stripe payments dashboard, rendered at /account/payments (nested
 * under UserHomepage). Three cards: payment account setup/status (create, onboard
 * via Stripe-hosted link, or disconnect Stripe customer + Connect accounts), and
 * paginated lists of received and sent payments. All data goes through
 * api/userPayment (UserPaymentAPI: getReceivedPayments, getSentPayments,
 * setupAccounts, disconnectAccounts, createOnboardingLink); account changes sync
 * into contexts/UserContext. Prompts via components/AlertDialog to add an email
 * (redirecting to /verifyEmail) before Stripe setup, and to confirm disconnects.
 */
import React, { useState, useEffect } from "react";
import { SubheadedPage } from "@/components/SubheadedPage";
import LoadingSpinner from "@/components/LoadingSpinner";
import { UserPaymentAPI } from "@/api/userPayment";
import { Pagination } from "@/types/pagination";
import {
    CreditCard,
    ArrowDown,
    ArrowUp,
    ExternalLink,
    CheckCircle,
    XCircle,
    Clock,
    Link2Off,
} from "lucide-react";
import { useUserDispatch, useUserState } from "@/contexts/UserContext";
import { useNavigate } from "react-router-dom";
import AlertDialog from "@/components/AlertDialog";
import { PaymentBountySubmission } from "@/types/payment";
import { setUserContext } from "@/contexts/UserContext";

const PaymentsPage: React.FC = () => {
    const user = useUserState();
    const dispatch = useUserDispatch();
    const navigate = useNavigate();
    const [receivedPayments, setReceivedPayments] = useState<PaymentBountySubmission[]>([]);
    const [receivedPagination, setReceivedPagination] = useState<Pagination | null>(null);
    const [sentPayments, setSentPayments] = useState<PaymentBountySubmission[]>([]);
    const [sentPagination, setSentPagination] = useState<Pagination | null>(null);
    const [showEmailDialog, setShowEmailDialog] = useState(false);
    const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

    const [accountSetupLoading, setAccountSetupLoading] = useState(false);
    const [receivedLoading, setReceivedLoading] = useState(false);
    const [sentLoading, setSentLoading] = useState(false);

    const buttonClasses =
        "flex justify-center py-2 px-4 text-sm font-medium text-black bg-white border border-gray-200 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:opacity-50 disabled:cursor-not-allowed font-porsui";
    const cardClasses =
        "bg-white border border-gray-200 rounded-lg p-6 shadow-sm min-w-[320px] relative";

    // Effects

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (!user?.email) {
            setShowEmailDialog(true);
        }
    }, []);

    // API calls

    const loadData = async () => {
        try {
            // Load received payments
            setReceivedLoading(true);
            const receivedData = await UserPaymentAPI.getReceivedPayments(user!.id, {
                page: 1,
                perPage: 10,
            });
            setReceivedPayments(receivedData.paymentBountySubmissions);
            setReceivedPagination(receivedData.pagination);
            setReceivedLoading(false);

            // Load sent payments
            setSentLoading(true);
            const sentData = await UserPaymentAPI.getSentPayments(user!.id, {
                page: 1,
                perPage: 10,
            });
            setSentPayments(sentData.paymentBountySubmissions);
            setSentPagination(sentData.pagination);
            setSentLoading(false);
        } catch (error) {
            console.error("Failed to load payment data:", error);
            setReceivedLoading(false);
            setSentLoading(false);
        }
    };

    const handleSetupAccounts = async () => {
        setAccountSetupLoading(true);
        try {
            const { user: updatedUser } = await UserPaymentAPI.setupAccounts(user!.id);
            setUserContext(dispatch, updatedUser);
        } catch (error) {
            console.error("Failed to setup accounts:", error);
        } finally {
            setAccountSetupLoading(false);
        }
    };

    const handleDisconnectAccounts = async () => {
        setShowDisconnectDialog(false);
        setAccountSetupLoading(true);
        try {
            const { user: updatedUser } = await UserPaymentAPI.disconnectAccounts(user!.id);
            setUserContext(dispatch, updatedUser);
            window.location.reload(); // Reload to reflect changes
        } catch (error) {
            console.error("Failed to disconnect accounts:", error);
        } finally {
            setAccountSetupLoading(false);
        }
    };

    const handleStartOnboarding = async () => {
        try {
            setAccountSetupLoading(true);
            const result = await UserPaymentAPI.createOnboardingLink(user!.id, {
                refreshUrl: window.location.href,
                returnUrl: window.location.href,
            });

            // Open onboarding in new window
            window.open(result.onboardingUrl, "_blank");
        } catch (error) {
            console.error("Failed to create onboarding link:", error);
        } finally {
            setAccountSetupLoading(false);
        }
    };

    const loadSentPage = async (page: number) => {
        try {
            const data = await UserPaymentAPI.getSentPayments(user!.id, {
                page,
                perPage: 10,
            });
            setSentPayments(data.paymentBountySubmissions);
            setSentPagination(data.pagination);
        } catch (error) {
            console.error("Failed to load sent payments:", error);
        }
    };

    const loadReceivedPage = async (page: number) => {
        try {
            const data = await UserPaymentAPI.getReceivedPayments(user!.id, {
                page,
                perPage: 10,
            });
            setReceivedPayments(data.paymentBountySubmissions);
            setReceivedPagination(data.pagination);
        } catch (error) {
            console.error("Failed to load received payments:", error);
        }
    };

    // UI helpers

    const formatAmount = (amount: number, currency: string) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency.toUpperCase(),
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const PaymentRow: React.FC<{
        payment: PaymentBountySubmission;
        isIncoming: boolean;
    }> = ({ payment, isIncoming }) => (
        <div className="flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50">
            <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-full`}>
                    {isIncoming ? (
                        <ArrowDown className="w-4 h-4 text-slate-950" />
                    ) : (
                        <ArrowUp className="w-4 h-4 text-slate-950" />
                    )}
                </div>
                <div>
                    <div className="font-medium text-sm text-gray-900">{payment.bounty.title}</div>
                    <div className="text-xs text-gray-400">
                        {payment.payment.paymentDate
                            ? formatDate(payment.payment.paymentDate)
                            : "not paid yet"}
                    </div>
                </div>
            </div>
            <div className="flex items-center space-x-3">
                <div className="text-right">
                    <div className="font-semibold text-sm text-gray-900">
                        {isIncoming
                            ? formatAmount(payment.payment.payoutAmount, "USD")
                            : formatAmount(payment.payment.chargeAmount, "USD")}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <SubheadedPage heading="payments" headerColor="bg-slate-50">
            <div className="w-full max-w-4xl space-y-8 p-4 sm:p-8">
                {showDisconnectDialog && (
                    <AlertDialog
                        title="disconnect payment accounts"
                        message="are you sure you want to disconnect your payment accounts? this will remove your ability to send and receive payments."
                        confirmText="disconnect"
                        cancelText="cancel"
                        onConfirm={handleDisconnectAccounts}
                        onCancel={() => setShowDisconnectDialog(false)}
                    />
                )}

                {/* Section 1: Account Setup/Status */}
                <div className={cardClasses}>
                    {accountSetupLoading && (
                        <div className="absolute inset-0 bg-white/50 flex items-center justify-center rounded-lg z-10">
                            <LoadingSpinner />
                        </div>
                    )}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                            <CreditCard className="w-5 h-5 text-gray-600" />
                            <h2 className="text-lg font-semibold text-gray-900 font-porsui">
                                payment account
                            </h2>
                        </div>
                        {user?.stripeConnectAccountId && user?.stripeCustomerId && (
                            <div className="relative group">
                                <button
                                    onClick={() => setShowDisconnectDialog(true)}
                                    disabled={accountSetupLoading}
                                >
                                    <Link2Off className="w-6 h-6 text-red-600 hover:text-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" />
                                </button>
                                <div className="absolute bottom-full right-0 mb-2 w-64 p-2 bg-slate-950 text-white text-sm rounded shadow-lg z-50 whitespace-pre-line opacity-0 group-hover:opacity-100 transition-opacity">
                                    disconnect and delete your porsui stripe accounts
                                </div>
                            </div>
                        )}
                    </div>

                    {!user?.stripeCustomerId ? (
                        <div className="flex flex-col items-center justify-center text-center py-8 space-y-4">
                            <button
                                onClick={handleSetupAccounts}
                                className={buttonClasses}
                                disabled={accountSetupLoading}
                            >
                                set up&nbsp;
                                <span className="text-black font-semibold">Stripe</span>
                                &nbsp;account
                            </button>
                            <p className="text-gray-600">
                                set up your stripe account to receive bounty rewards
                            </p>
                        </div>
                    ) : !user?.stripeOnboardingCompleted ? (
                        <div className="text-center py-8">
                            <div className="mb-4">
                                <div className="text-sm text-gray-600 mb-2">account status:</div>
                                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-black">
                                    <Clock className="w-4 h-4 mr-1" />
                                    onboarding required
                                </div>
                            </div>
                            <p className="text-gray-600 mb-4">
                                complete your payment verification to receive bounty rewards
                            </p>
                            <button
                                onClick={handleStartOnboarding}
                                disabled={accountSetupLoading}
                                className="inline-flex items-center justify-center text-center py-2 px-4 text-sm font-medium text-black bg-white border border-gray-200 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:opacity-50 disabled:cursor-not-allowed font-porsui w-auto mx-auto"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <ExternalLink className="w-4 h-4 mr-2" />
                                complete verification
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-3">
                                <div>
                                    <div className="text-sm text-gray-600 mb-1">account status</div>
                                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-50 text-black">
                                        <CheckCircle className="w-4 h-4 mr-1" />
                                        verified
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-600 mb-1">customer id</div>
                                    <div className="font-mono text-xs text-gray-800">
                                        {user!.stripeCustomerId}
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <div>
                                    <div className="text-sm text-gray-600 mb-1">
                                        payments enabled
                                    </div>
                                    <div
                                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                                            user!.stripeOnboardingCompleted
                                                ? "bg-green-50 text-black"
                                                : "bg-red-50 text-red-600"
                                        }`}
                                    >
                                        {user!.stripeOnboardingCompleted ? (
                                            <CheckCircle className="w-4 h-4 mr-1" />
                                        ) : (
                                            <XCircle className="w-4 h-4 mr-1" />
                                        )}
                                        {user!.stripeOnboardingCompleted ? "enabled" : "disabled"}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-600 mb-1">
                                        connect account id
                                    </div>
                                    <div className="font-mono text-xs text-gray-800">
                                        {user!.stripeConnectAccountId}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Section 2: Received Payments */}
                <div className={cardClasses}>
                    {receivedLoading && (
                        <div className="absolute inset-0 bg-white/50 flex items-center justify-center rounded-lg z-10">
                            <LoadingSpinner />
                        </div>
                    )}
                    <div className="flex items-center space-x-3 mb-4">
                        <ArrowDown className="w-5 h-5 text-black" />
                        <h2 className="text-lg font-semibold text-gray-900 font-porsui">
                            received payments
                        </h2>
                        {receivedPayments && (
                            <span className="text-sm text-gray-500">
                                ({receivedPagination?.totalCount} total)
                            </span>
                        )}
                    </div>

                    {!receivedPayments?.length ? (
                        <div className="text-center py-8 text-gray-500">
                            no received payments yet
                        </div>
                    ) : (
                        <>
                            <div className="divide-y divide-gray-100">
                                {receivedPayments.map((paymentBountySubmission) => (
                                    <PaymentRow
                                        key={paymentBountySubmission.payment.id}
                                        payment={paymentBountySubmission}
                                        isIncoming={true}
                                    />
                                ))}
                            </div>

                            {receivedPagination && receivedPagination.pageCount > 1 && (
                                <div className="flex items-center justify-center space-x-2 mt-6 pt-4 border-t border-gray-100">
                                    <button
                                        onClick={() =>
                                            loadReceivedPage(
                                                (receivedPagination?.currentPage ?? 1) - 1
                                            )
                                        }
                                        disabled={!receivedPagination?.hasPrev}
                                        className={`px-3 py-1 text-sm border border-gray-200 rounded ${
                                            receivedPagination?.hasPrev
                                                ? "hover:bg-gray-50 text-gray-700"
                                                : "text-gray-400 cursor-not-allowed"
                                        }`}
                                    >
                                        previous
                                    </button>

                                    <span className="text-sm text-gray-600">
                                        page {receivedPagination?.currentPage} of{" "}
                                        {receivedPagination?.pageCount}
                                    </span>

                                    <button
                                        onClick={() =>
                                            loadReceivedPage(
                                                (receivedPagination?.currentPage ?? 1) + 1
                                            )
                                        }
                                        disabled={!receivedPagination?.hasNext}
                                        className={`px-3 py-1 text-sm border border-gray-200 rounded ${
                                            receivedPagination && receivedPagination.hasNext
                                                ? "hover:bg-gray-50 text-gray-700"
                                                : "text-gray-400 cursor-not-allowed"
                                        }`}
                                    >
                                        next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Section 3: Sent Payments */}
                <div className={cardClasses}>
                    {sentLoading && (
                        <div className="absolute inset-0 bg-white/50 flex items-center justify-center rounded-lg z-10">
                            <LoadingSpinner />
                        </div>
                    )}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                            <ArrowUp className="w-5 h-5 text-slate-950" />
                            <h2 className="text-lg font-semibold text-gray-900 font-porsui">
                                sent payments
                            </h2>
                            {sentPayments && (
                                <span className="text-sm text-gray-500">
                                    ({sentPagination?.totalCount} total)
                                </span>
                            )}
                        </div>
                    </div>

                    {!sentPayments?.length ? (
                        <div className="text-center py-8 text-gray-500">no sent payments yet</div>
                    ) : (
                        <>
                            <div className="divide-y divide-gray-100">
                                {sentPayments.map((payment) => (
                                    <PaymentRow
                                        key={payment.payment.id}
                                        payment={payment}
                                        isIncoming={false}
                                    />
                                ))}
                            </div>

                            {sentPagination && sentPagination.pageCount > 1 && (
                                <div className="flex items-center justify-center space-x-2 mt-6 pt-4 border-t border-gray-100">
                                    <button
                                        onClick={() =>
                                            loadSentPage((sentPagination?.currentPage ?? 1) - 1)
                                        }
                                        disabled={!sentPagination?.hasPrev}
                                        className={`px-3 py-1 text-sm border border-gray-200 rounded ${
                                            sentPagination?.hasPrev
                                                ? "hover:bg-gray-50 text-gray-700"
                                                : "text-gray-400 cursor-not-allowed"
                                        }`}
                                    >
                                        previous
                                    </button>

                                    <span className="text-sm text-gray-600">
                                        page {sentPagination?.currentPage} of{" "}
                                        {sentPagination?.pageCount}
                                    </span>

                                    <button
                                        onClick={() =>
                                            loadSentPage((sentPagination?.currentPage ?? 1) + 1)
                                        }
                                        disabled={!sentPagination?.hasNext}
                                        className={`px-3 py-1 text-sm border border-gray-200 rounded ${
                                            sentPagination?.hasNext
                                                ? "hover:bg-gray-50 text-gray-700"
                                                : "text-gray-400 cursor-not-allowed"
                                        }`}
                                    >
                                        next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
                {showEmailDialog && (
                    <AlertDialog
                        title="email required"
                        message="please enter your email in order to setup your stripe account"
                        cancelText="not now"
                        confirmText="ok"
                        onConfirm={() => {
                            setShowEmailDialog(false);
                            navigate("/verifyEmail");
                        }}
                        onCancel={() => {
                            setShowEmailDialog(false);
                            navigate("/account/profile");
                        }}
                    />
                )}
            </div>
        </SubheadedPage>
    );
};

export default PaymentsPage;
